import PropTypes from 'prop-types'
import { Button, Switch } from 'antd'
import { CalendarOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { formatDOB } from '../../../lib/datetime'
import filenameFromBlobPath from '../../../lib/active-storage'
import { toPath } from '../../../routes'
import DownloadLink from '../../Common/DownloadLink'
import PatientEntry from '../../Common/PatientEntry'

// Width of antd's expand-icon column. The expanded details are indented by the
// same amount so their fields line up with the card above them.
export const EXPAND_COLUMN_WIDTH = 40

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
    // PatientEntry sets the link to nowrap for the desktop table; on mobile we
    // let a long name wrap instead of overflowing.
    '& a': {
      whiteSpace: 'normal !important',
    },
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#8c8c8c',
  },
  // Billed switch + details link share the last line. Left-aligned rather than
  // space-between: the switch label swaps between "Billed" and "Not Billed", so
  // pinning it right would make the edge jitter from row to row.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    marginTop: 4,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 16px',
    paddingLeft: EXPAND_COLUMN_WIDTH,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  fieldValue: {
    fontSize: 14,
    color: '#1c1c1c',
    overflowWrap: 'anywhere',
  },
  // Lets a field take the full width of the 2-column details grid.
  spanTwo: {
    gridColumn: '1 / -1',
  },
})

function Field({ label, children = null, span = false }) {
  const classes = useStyles()
  return (
    <div
      className={span ? `${classes.field} ${classes.spanTwo}` : classes.field}
    >
      <span className={classes.fieldLabel}>{label}</span>
      <span className={classes.fieldValue}>{children}</span>
    </div>
  )
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  span: PropTypes.bool,
}

export function BillingOrderMobileCard({ billingOrder, onBilledChange }) {
  const classes = useStyles()
  const { billingDate, cptCode, device, id, patient } = billingOrder
  // formatDOB(undefined) would render today's date, so only format real values.
  const dob = patient?.birthDate ? formatDOB(patient.birthDate) : null
  const patientLine = [dob, device?.deviceModel?.deviceType?.name]
    .filter(Boolean)
    .join(' · ')
  const billingLine = [
    billingDate ? formatDOB(billingDate) : null,
    cptCode ? `CPT ${cptCode}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>
        {patient ? <PatientEntry patient={patient} /> : '—'}
      </div>
      {patientLine && <div className={classes.line}>{patientLine}</div>}
      {billingLine && (
        <div className={classes.line}>
          <CalendarOutlined className={classes.lineIcon} />
          {billingLine}
        </div>
      )}
      <div className={classes.actionsLine}>
        <Switch
          checked={billingOrder.billed}
          checkedChildren="Billed"
          unCheckedChildren="Not&nbsp;Billed"
          onChange={billed => onBilledChange(id, billed)}
        />
        <Link
          to={toPath.billing.officeBillingOrders.officeBillingOrder({ id })}
        >
          <Button
            type="link"
            size="small"
            icon={<FileSearchOutlined />}
            style={{ paddingLeft: 0 }}
          >
            Comments / More
          </Button>
        </Link>
      </div>
    </div>
  )
}

BillingOrderMobileCard.propTypes = {
  billingOrder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billed: PropTypes.bool,
    billingDate: PropTypes.string,
    cptCode: PropTypes.string,
    patient: PropTypes.shape({
      birthDate: PropTypes.string,
    }),
    device: PropTypes.shape({
      deviceModel: PropTypes.shape({
        deviceType: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  onBilledChange: PropTypes.func.isRequired,
}

export function BillingOrderMobileDetails({ billingOrder }) {
  const classes = useStyles()
  const { icd10Code, patient, preAuthorization } = billingOrder

  // Same rule as the desktop "Auth #" column: link the number to the
  // authorization file when there is one, otherwise show it as plain text.
  let authNumber = '—'
  if (preAuthorization) {
    const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
      preAuthorization
    if (insuranceAuthorizationFilePath) {
      authNumber = (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>
            {insuranceAuthorizationNumber ||
              filenameFromBlobPath(insuranceAuthorizationFilePath)}
          </span>
        </DownloadLink>
      )
    } else {
      authNumber = insuranceAuthorizationNumber || '—'
    }
  }

  return (
    <div className={classes.detailsGrid}>
      <Field label="MRN">{patient?.practiceMrn || '—'}</Field>
      <Field label="ICD-10 code">{icd10Code || '—'}</Field>
      <Field label="Auth #" span>
        {authNumber}
      </Field>
    </div>
  )
}

BillingOrderMobileDetails.propTypes = {
  billingOrder: PropTypes.shape({
    icd10Code: PropTypes.string,
    patient: PropTypes.shape({
      practiceMrn: PropTypes.string,
    }),
    preAuthorization: PropTypes.shape({
      insuranceAuthorizationFilePath: PropTypes.string,
      insuranceAuthorizationNumber: PropTypes.string,
    }),
  }).isRequired,
}
